import React from 'react';
import { Body, Button, Flex, Loader, Title, Tooltip } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { InfoMessage } from 'components/InfoMessage/InfoMessage';
import { SeverityTag } from 'components/SeverityTag/SeverityTag';
import { formatDateRecentTime } from 'utils/misc';
import { getValidityColor } from 'utils/runStatistics';
import { ConditionsTreeSummary } from '../components/Schemas/ViewSchemaPage/ProjectRuleset/components/Conditions/ConditionsTreeSummary';
import { FailedInstancesTable } from './components/FailedInstancesTable';
import { RuleOperatorLabel } from './consistencyReportTypes';
import { useConsistencyRuleReportPage } from './hooks/useConsistencyRuleReportPage';

export const ViewConsistencyRuleReportPage = () => {
  const {
    report,
    rule,
    loading,
    error,
    instances,
    instancesLoading,
    instancesError,
    successRate,
    ruleId,
    consistencyReportId,
    sourceFilterTree,
    targetFilterTree,
    conditionCheckLabels,
    handleGoBack,
    handleDownload,
  } = useConsistencyRuleReportPage();

  if (loading) {
    return (
      <StyledPage
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Loader />
      </StyledPage>
    );
  }

  if (error || !report || !rule) {
    return (
      <StyledPage direction="column">
        <InfoMessage type="Search" title="Rule report not found">
          <span>
            {error
              ? `Error loading report: ${error.message}`
              : `No rule with id ${ruleId} in report ${consistencyReportId}.`}
          </span>
        </InfoMessage>
      </StyledPage>
    );
  }

  return (
    <StyledPage direction="column">
      <StyledHeader direction="row" alignItems="center" gap={10}>
        <Button
          icon="ArrowLeft"
          iconPlacement="left"
          onClick={handleGoBack}
          type="ghost"
          aria-label="Go back"
        />
        <Title level={4}>{report.collectionName}</Title>
        <Separator>&middot;</Separator>
        <RuleTitle level={5}>{rule.ruleName}</RuleTitle>
      </StyledHeader>

      <SplitLayout>
        <LeftPane>
          <Flex
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <div>
              <Title level={5}>
                Run result {formatDateRecentTime(report.runAt)}
              </Title>
              <Body size="small">
                {rule.totalFailed} failed instances &middot; {successRate}%
                valid
              </Body>
            </div>
          </Flex>

          {instancesError && rule.totalFailed > 0 && (
            <ErrorBanner size="small">
              Could not load failed instances: {instancesError}
            </ErrorBanner>
          )}

          <FailedInstancesTable
            instances={instances}
            loading={instancesLoading}
          />
        </LeftPane>

        <RightPane>
          <RuleName level={4}>{rule.ruleName}</RuleName>

          {rule.errorMessage && (
            <Section>
              <SectionLabel size="x-small">ERROR MESSAGE</SectionLabel>
              <Body size="medium" strong>
                {rule.errorMessage}
              </Body>
            </Section>
          )}

          <StatsSection>
            <ValidityRing
              percentage={successRate}
              color={getValidityColor(successRate)}
            >
              <RingValue>{successRate}%</RingValue>
              <RingLabel>valid</RingLabel>
            </ValidityRing>
            <Flex direction="column" gap={6}>
              <Body size="x-small">
                Filtered instances checked{' '}
                <b>{rule.totalValid + rule.totalFailed}</b>
              </Body>
              <Body size="x-small">
                Failed instances <b>{rule.totalFailed}</b>
              </Body>
            </Flex>
          </StatsSection>

          <Tooltip
            content="Download all failed instances as JSON"
            disabled={instances.length === 0}
          >
            <Button
              icon="Download"
              disabled={instances.length === 0 || instancesLoading}
              onClick={handleDownload}
            >
              Download .json
            </Button>
          </Tooltip>

          <Section>
            <SectionLabel size="x-small">COMPARISON</SectionLabel>
            <Body size="small">
              {rule.sourceSchemaFilterName}.{rule.sourceAttribute}
            </Body>
            <OperatorLine size="small" strong>
              {RuleOperatorLabel[rule.operator]}
            </OperatorLine>
            <Body size="small">
              {rule.targetSchemaFilterName}.{rule.targetAttribute}
            </Body>
          </Section>

          {sourceFilterTree && (
            <Section>
              <SectionLabel size="x-small">SOURCE FILTERS</SectionLabel>
              <ConditionsTreeSummary
                prefix={`source-filters-${rule.ruleId}`}
                conditionsTree={sourceFilterTree}
              />
            </Section>
          )}

          {targetFilterTree && (
            <Section>
              <SectionLabel size="x-small">TARGET FILTERS</SectionLabel>
              <ConditionsTreeSummary
                prefix={`target-filters-${rule.ruleId}`}
                conditionsTree={targetFilterTree}
              />
            </Section>
          )}

          {conditionCheckLabels.length > 0 && (
            <Section>
              <SectionLabel size="x-small">CONDITION CHECKS</SectionLabel>
              {conditionCheckLabels.map(({ key, label }) => (
                <Body size="small" key={key}>
                  {label}
                </Body>
              ))}
            </Section>
          )}

          <Section>
            <SectionLabel size="x-small">SEVERITY</SectionLabel>
            <SeverityTag severity={rule.severity} />
          </Section>
        </RightPane>
      </SplitLayout>
    </StyledPage>
  );
};

const StyledPage = styled(Flex)`
  flex: 1;
  overflow: hidden;
  background: var(--cogs-white);
`;

const StyledHeader = styled(Flex)`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--cogs-border-default);
`;

const Separator = styled.span`
  color: var(--cogs-greyscale-grey5);
  padding: 0 0.25rem;
`;

const RuleTitle = styled(Title)`
  color: var(--cogs-greyscale-grey7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: 70% 30%;
  flex: 1;
  overflow: hidden;
`;

const LeftPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem;
  overflow: auto;
  background: var(--cogs-bg-accent);
`;

const RightPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  overflow: auto;
  border-left: 1px solid var(--cogs-border-default);
`;

const RuleName = styled(Title)`
  margin: 0.25rem 0 0.75rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SectionLabel = styled(Body)`
  color: var(--cogs-greyscale-grey7);
  letter-spacing: 0.02em;
`;

const StatsSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
`;

const ValidityRing = styled.div<{ percentage: number; color: string }>`
  --p: ${({ percentage }) => percentage};
  --c: ${({ color }) => color};
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: conic-gradient(
    var(--c) calc(var(--p) * 1%),
    var(--cogs-greyscale-grey3) 0
  );
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 8px;
    border-radius: 50%;
    background: var(--cogs-white);
  }
`;

const RingValue = styled.span`
  position: relative;
  font-size: 1.25rem;
  font-weight: 700;
`;

const RingLabel = styled.span`
  position: relative;
  font-size: 0.75rem;
  color: var(--cogs-greyscale-grey7);
`;

const OperatorLine = styled(Body)`
  color: var(--cogs-primary);
`;

const ErrorBanner = styled(Body)`
  padding: 0.5rem 0.75rem;
  color: var(--cogs-text-danger);
  background: var(--cogs-red-light, #fff1f0);
  border: 1px solid var(--cogs-red, #db0657);
  border-radius: 4px;
`;
